import React, {useCallback, useState, useEffect} from 'react';
import * as THREE from 'three';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import FloatingMenuButton, {
    FloatingMenuButtonIcon,
} from 'components/customer/RoomPlanner/components/common/FloatingMenuButton';
import {UserData} from 'components/customer/RoomPlanner/types';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {useTheme} from 'styled-components';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';

const Movement = () => {
    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();
    const {selectedCabinetGroup, setMovementHandler} = useRoomPlannerContext();
    const [isMovementMode, setIsMovementMode] = useState(false);
    const theme = useTheme();

    // Sync movement mode state with the active cabinet when it changes
    useEffect(() => {
        if (selectedCabinetGroup) {
            // Get the current movement mode state from the cabinet
            const currentMovementMode =
                !!selectedCabinetGroup.userData.isMovementMode;

            // Update local state if it doesn't match the cabinet's state
            if (isMovementMode !== currentMovementMode) {
                setIsMovementMode(currentMovementMode);

                // Make sure arrows are shown/hidden appropriately
                const userData = selectedCabinetGroup.userData as {
                    setActiveState?: (isActive: boolean) => void;
                    setDistanceMeasurementsActive?: (isActive: boolean) => void;
                };

                if (typeof userData?.setActiveState === 'function') {
                    userData.setActiveState(currentMovementMode);
                }

                if (
                    typeof userData?.setDistanceMeasurementsActive ===
                    'function'
                ) {
                    userData.setDistanceMeasurementsActive(currentMovementMode);
                }
            }
        } else {
            // Reset state when no cabinet is selected
            setIsMovementMode(false);
        }
    }, [selectedCabinetGroup]); // Removed isMovementMode from dependencies to prevent infinite loop

    const handleMovementClick = useCallback(
        (event?: React.MouseEvent<HTMLButtonElement>) => {
            // `event` may not exist if called from a non-mouse event (e.g., key press 'M')
            // Remove focus from the button to prevent keyboard events from being captured
            event?.currentTarget.blur();
            setIsMovementMode((prev) => !prev);
            if (selectedCabinetGroup) {
                selectedCabinetGroup.userData.isMovementMode = !isMovementMode;
                const userDataWithArrowGroup =
                    selectedCabinetGroup.userData as UserData & {
                        arrowGroup?: THREE.Group;
                    };
                if (userDataWithArrowGroup.arrowGroup) {
                    const arrowGroup = userDataWithArrowGroup.arrowGroup;
                    if (arrowGroup.children) {
                        arrowGroup.children.forEach((arrow: THREE.Object3D) => {
                            if (
                                arrow.userData &&
                                arrow.userData.isArrow &&
                                arrow.userData.isHovered
                            ) {
                                if (
                                    typeof arrow.userData.onHoverEnd ===
                                    'function'
                                ) {
                                    (
                                        arrow.userData as {
                                            onHoverEnd: () => void;
                                        }
                                    ).onHoverEnd();
                                }
                                arrow.userData.isHovered = false;
                            }
                        });
                    }
                }
                const userData = selectedCabinetGroup.userData as {
                    setActiveState?: (isActive: boolean) => void;
                    setDistanceMeasurementsActive?: (isActive: boolean) => void;
                };
                if (typeof userData?.setActiveState === 'function') {
                    userData.setActiveState(!isMovementMode);
                }
                if (
                    typeof userData?.setDistanceMeasurementsActive ===
                    'function'
                ) {
                    userData.setDistanceMeasurementsActive(!isMovementMode);
                }
            }
        },
        [selectedCabinetGroup, isMovementMode]
    );

    // Register the movement handler with the context for keyboard shortcut
    useEffect(() => {
        if (setMovementHandler) {
            setMovementHandler(handleMovementClick);
        }
        return () => setMovementHandler?.(null);
    }, [handleMovementClick, setMovementHandler]);

    return (
        <OverlayTrigger placement="top" overlay={<strong>Move (M)</strong>}>
            <FloatingMenuButton
                onClick={handleMovementClick}
                className={isMovementMode ? 'active' : ''}>
                <FloatingMenuButtonIcon
                    className="icon_button"
                    color={
                        isMovementMode
                            ? '#29b6f6'
                            : isMobileLandscape || isPortraitDevices
                              ? theme.colors.primaryIconColor.main
                              : '#b2bec3'
                    }
                    iconName={
                        isMobileLandscape || isPortraitDevices
                            ? 'Button-Drag-white.svg'
                            : 'Button-Drag.svg'
                    }
                />
                <label>Move</label>
            </FloatingMenuButton>
        </OverlayTrigger>
    );
};

export default Movement;
